import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import {
  SESSION_FORMAT_PRICES_SUBUNITS,
  SESSION_LENGTH_MINUTES,
  offeredFormatsFromListing,
  resolveSessionFormat,
  sessionFormatDisplayName,
  talkSessionPriceVariants,
  variantNameForFormat,
} from '../../util/sessionPricing';
import { listingCardDisplayName } from '../../util/listingCardCopy';
import { bookingTimes } from '../../util/sessionClock';
import { listingTimeZone, parseDateTimeString } from '../../util/dates';
import { ACCEPT_BEFORE_START_MS } from '../../util/acceptDeadline';
import {
  bookingPageIdAfterEdit,
  editBookingErrorMessage,
  EDIT_BOOKING_CODES,
} from '../../util/editUpcomingBooking';
import { editUpcomingBooking, fetchEditBookingSlots } from '../../util/api';

import Form from '../Form/Form';
import Modal from '../Modal/Modal';
import { PrimaryButton, SecondaryButton } from '../Button/Button';
import SessionFormatPicker from '../OrderPanel/SessionFormatPicker/SessionFormatPicker';

import css from './EditBookingModal.module.css';

const SESSION_LENGTH_MS = SESSION_LENGTH_MINUTES * 60 * 1000;

const moneyLabel = subunits => `$${Math.round((subunits || 0) / 100)}`;

const asDate = value => {
  if (!value && value !== 0) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms) : null;
};

const ceilToQuarter = ms => Math.ceil(ms / SESSION_LENGTH_MS) * SESSION_LENGTH_MS;

/**
 * Calendar day (YYYY-MM-DD) for a Date in the listing timezone.
 * Uses formatToParts so Safari cannot turn en-CA into an invalid date value.
 *
 * @param {Date} date
 * @param {string} timeZone
 * @returns {string}
 */
export const ymdInZone = (date, timeZone) => {
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const pick = type => parts.find(part => part.type === type)?.value || '';
  const year = pick('year');
  const month = pick('month');
  const day = pick('day');
  return year && month && day ? `${year}-${month}-${day}` : '';
};

/**
 * True when format or the 15-minute start differs from the current booking.
 *
 * @param {Object} params
 * @returns {boolean}
 */
export const isEditBookingDirty = ({
  nextFormat,
  currentFormat,
  selectedStart,
  currentStart,
  dateKey,
  currentDateKey,
  bookingStartTime,
} = {}) => {
  if (nextFormat && currentFormat && nextFormat !== currentFormat) {
    return true;
  }
  if (dateKey && currentDateKey && dateKey !== currentDateKey) {
    return true;
  }
  if (
    bookingStartTime &&
    currentStart &&
    String(currentStart.getTime()) !== String(bookingStartTime)
  ) {
    return true;
  }
  return false;
};

/**
 * Keep the already-booked start in the time list even when that seat
 * is held by this booking (timeslots then show seats=0).
 *
 * @param {Date[]} starts
 * @param {Date|null} currentStart
 * @param {string} dateKey
 * @param {string} timeZone
 * @param {Date} [now]
 * @returns {Date[]}
 */
export const withCurrentStart = (starts, currentStart, dateKey, timeZone, now = new Date()) => {
  if (!currentStart || Number.isNaN(currentStart.getTime())) {
    return starts;
  }
  if (currentStart.getTime() <= now.getTime()) {
    return starts;
  }
  if (ymdInZone(currentStart, timeZone) !== dateKey) {
    return starts;
  }
  if (starts.some(start => start.getTime() === currentStart.getTime())) {
    return starts;
  }
  return [...starts, currentStart].sort((a, b) => a.getTime() - b.getTime());
};

/**
 * Prefer the picked start. If the date changed and no time is picked yet,
 * do not keep the old booking's clock. Same-day empty select still falls
 * back to the booked window so format-only saves work.
 *
 * @param {string|number|null} bookingStartTime
 * @param {Date|null} currentStart
 * @param {{ dateKey?: string, currentDateKey?: string }} [opts]
 * @returns {Date|null}
 */
export const resolveEditStart = (bookingStartTime, currentStart, opts = {}) => {
  const startMs = Number(bookingStartTime);
  if (Number.isFinite(startMs) && startMs > 0) {
    const picked = new Date(startMs);
    return Number.isNaN(picked.getTime()) ? null : picked;
  }
  if (opts.dateKey && opts.currentDateKey && opts.dateKey !== opts.currentDateKey) {
    return null;
  }
  return currentStart || null;
};

export const quarterHourStartsOnDate = (slots, dateKey, timeZone, now = new Date()) => {
  const starts = [];
  const seen = new Set();
  const earliest = ceilToQuarter(now.getTime() + ACCEPT_BEFORE_START_MS);
  (slots || []).forEach(slot => {
    const start = asDate(slot?.attributes?.start || slot?.start);
    const end = asDate(slot?.attributes?.end || slot?.end);
    const seats = slot?.attributes?.seats ?? slot?.seats ?? 0;
    if (!start || !end || seats < 1) {
      return;
    }
    let cursor = ceilToQuarter(start.getTime());
    while (cursor + SESSION_LENGTH_MS <= end.getTime()) {
      if (cursor >= earliest) {
        const at = new Date(cursor);
        if (ymdInZone(at, timeZone) === dateKey && !seen.has(cursor)) {
          seen.add(cursor);
          starts.push(at);
        }
      }
      cursor += SESSION_LENGTH_MS;
    }
  });
  return starts.sort((a, b) => a.getTime() - b.getTime());
};

const WEEKDAY_KEYS = {
  Sun: 'sun',
  Mon: 'mon',
  Tue: 'tue',
  Wed: 'wed',
  Thu: 'thu',
  Fri: 'fri',
  Sat: 'sat',
};

/**
 * Listing-plan weekday key (sun…sat) for a YYYY-MM-DD calendar day.
 *
 * @param {string} dateKey
 * @param {string} timeZone
 * @returns {string}
 */
export const weekdayKeyOnDate = (dateKey, timeZone) => {
  const at = middayOnDateKey(dateKey);
  if (!at) {
    return '';
  }
  const dow = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone }).format(at);
  return WEEKDAY_KEYS[dow] || '';
};

/**
 * Open HH:mm windows from the listener weekly plan for one calendar day.
 * Sharetribe uses 00:00–00:00 as all day.
 *
 * @param {Object} [plan]
 * @param {string} dateKey
 * @param {string} timeZone
 * @returns {Array<{ start: Date, end: Date }>}
 */
export const planWindowsOnDate = (plan, dateKey, timeZone) => {
  const weekday = weekdayKeyOnDate(dateKey, timeZone);
  const entries = Array.isArray(plan?.entries) ? plan.entries : [];
  return entries
    .filter(entry => entry?.dayOfWeek === weekday && (entry.seats == null || entry.seats > 0))
    .map(entry => {
      const startHm = entry.startTime || entry.start || '';
      const rawEnd = entry.endTime || entry.end || '';
      const endHm = rawEnd === '00:00' ? '24:00' : rawEnd;
      if (!startHm || !endHm || !/^\d{2}:\d{2}$/.test(startHm)) {
        return null;
      }
      const start = parseDateTimeString(`${dateKey} ${startHm}`, timeZone);
      let end = endHm === '24:00'
        ? parseDateTimeString(
            `${dateKey} 00:00`,
            timeZone
          )
        : parseDateTimeString(`${dateKey} ${endHm}`, timeZone);
      if (endHm === '24:00' && end) {
        end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
      }
      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
      }
      return { start, end };
    })
    .filter(Boolean);
};

/**
 * Keep 15-minute starts that sit inside the listener's open hours
 * (same weekly plan /book uses). No plan → keep timeslot starts.
 *
 * @param {Date[]} starts
 * @param {Array<{ start: Date, end: Date }>} windows
 * @returns {Date[]}
 */
export const filterStartsByPlanWindows = (starts, windows) => {
  if (!Array.isArray(windows) || windows.length === 0) {
    return starts;
  }
  return (starts || []).filter(start =>
    windows.some(
      window =>
        start.getTime() >= window.start.getTime() &&
        start.getTime() + SESSION_LENGTH_MS <= window.end.getTime()
    )
  );
};

/**
 * Midday instant that still prints as YYYY-MM-DD in the listing zone.
 *
 * @param {string} dateKey
 * @returns {Date|null}
 */
export const middayOnDateKey = dateKey => {
  if (!dateKey || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
    return null;
  }
  const at = new Date(`${dateKey}T12:00:00.000Z`);
  return Number.isNaN(at.getTime()) ? null : at;
};

/**
 * Open calendar days (plus the current booking day) for day chips.
 *
 * @param {Array} slots
 * @param {string} timeZone
 * @param {Date|null} currentStart
 * @param {Date} [now]
 * @returns {string[]}
 */
export const openDateKeys = (slots, timeZone, currentStart, now = new Date()) => {
  const keys = new Set();
  const earliest = ceilToQuarter(now.getTime() + ACCEPT_BEFORE_START_MS);
  (slots || []).forEach(slot => {
    const start = asDate(slot?.attributes?.start || slot?.start);
    const end = asDate(slot?.attributes?.end || slot?.end);
    const seats = slot?.attributes?.seats ?? slot?.seats ?? 0;
    if (!start || !end || seats < 1) {
      return;
    }
    let cursor = ceilToQuarter(start.getTime());
    while (cursor + SESSION_LENGTH_MS <= end.getTime()) {
      if (cursor >= earliest) {
        keys.add(ymdInZone(new Date(cursor), timeZone));
      }
      cursor += SESSION_LENGTH_MS;
    }
  });
  if (currentStart && currentStart.getTime() > now.getTime()) {
    const currentKey = ymdInZone(currentStart, timeZone);
    if (currentKey) {
      keys.add(currentKey);
    }
  }
  return [...keys].filter(Boolean).sort();
};

/**
 * Navy summary copy, e.g. "Sat, Sep 26 · 10:00 PM".
 *
 * @param {Date|null} date
 * @param {Object} intl
 * @param {string} timeZone
 * @returns {string}
 */
export const selectedWhenLabel = (date, intl, timeZone) => {
  if (!date || Number.isNaN(date.getTime()) || !intl) {
    return '';
  }
  const day = intl.formatDate(date, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  });
  const time = intl.formatDate(date, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
  return `${day} · ${time}`;
};

const whenLabel = (date, intl, timeZone) => {
  if (!date) {
    return '';
  }
  return intl.formatDate(date, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
};

/**
 * 15-minute chips for one day. Auto-scrolls to the booked/selected time.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const TimeChipList = props => {
  const { times, selectedValue, bookedStart, timeZone, intl, onPick } = props;
  const listRef = useRef(null);
  const bookedMs = bookedStart && !Number.isNaN(bookedStart.getTime()) ? bookedStart.getTime() : null;

  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return undefined;
    }
    const chip =
      list.querySelector('[data-edit-booked="true"]') ||
      list.querySelector('[aria-checked="true"]');
    if (!chip) {
      return undefined;
    }
    const top = chip.offsetTop - list.clientHeight / 2 + chip.clientHeight / 2;
    list.scrollTop = Math.max(0, top);
    return undefined;
  }, [selectedValue, bookedMs, times.length]);

  if (times.length === 0) {
    return null;
  }

  return (
    <div
      className={css.timeChips}
      ref={listRef}
      role="radiogroup"
      aria-labelledby="edit-booking-time-label"
      data-edit-time-chips="true"
    >
      {times.map(start => {
        const value = String(start.getTime());
        const selected = selectedValue === value;
        const booked = bookedMs != null && start.getTime() === bookedMs;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={classNames(
              css.timeChip,
              selected ? css.timeChipSelected : null,
              booked ? css.timeChipBooked : null
            )}
            data-edit-time-chip={value}
            data-edit-booked={booked ? 'true' : undefined}
            onClick={() => onPick(value)}
          >
            {intl.formatDate(start, {
              hour: 'numeric',
              minute: '2-digit',
              timeZone,
            })}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Talker edit: format → date → 15-minute time → confirm (re-quote if price changes).
 * Success goes to OrderDetailsPage (/order/:id), the same booking page as checkout.
 * Failure stays on this form with an error.
 *
 * @param {Object} props
 * @returns {JSX.Element|null}
 */
const EditBookingModal = props => {
  const {
    isOpen,
    transaction,
    listing,
    onClose,
    onCancelBooking,
    onManageDisableScrolling,
    onEdited,
  } = props;
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const timeZone = listingTimeZone(listing?.attributes?.availabilityPlan?.timezone);
  const listenerName =
    listingCardDisplayName(listing) || listing?.attributes?.title || 'this listener';
  const currentFormat = resolveSessionFormat(transaction?.attributes?.protectedData || {});
  const { start: currentStart } = bookingTimes(transaction);
  const offeredFormats = offeredFormatsFromListing(listing);
  const priceVariants = talkSessionPriceVariants();
  const [slots, setSlots] = useState([]);
  const [slotsError, setSlotsError] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !listing?.id) {
      return undefined;
    }
    let cancelled = false;
    const rangeStart = new Date();
    const rangeEnd = new Date(rangeStart.getTime() + 32 * 24 * 60 * 60 * 1000);
    setSlotsLoading(true);
    setSlotsError(null);
    fetchEditBookingSlots({
      listingId: listing.id.uuid || listing.id,
      start: rangeStart,
      end: rangeEnd,
    })
      .then(payload => {
        if (cancelled) {
          return;
        }
        setSlots(payload?.data?.timeSlots || payload?.timeSlots || []);
        setSlotsLoading(false);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setSlotsError(EDIT_BOOKING_CODES.SEAT_UNAVAILABLE);
        setSlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, listing?.id]);

  const currentDateKey = currentStart ? ymdInZone(currentStart, timeZone) : '';
  const initialValues = useMemo(
    () => ({
      sessionFormat: currentFormat || offeredFormats[0] || 'chat',
      priceVariantName: variantNameForFormat(currentFormat || offeredFormats[0] || 'chat'),
      bookingDate: currentDateKey,
      bookingStartTime: currentStart ? String(currentStart.getTime()) : '',
    }),
    [currentFormat, currentDateKey, currentStart, offeredFormats]
  );

  if (!isOpen) {
    return null;
  }

  const onSubmit = values => {
    const format = values.sessionFormat;
    const start = resolveEditStart(values.bookingStartTime, currentStart, {
      dateKey: values.bookingDate,
      currentDateKey,
    });
    if (!format || !start) {
      setSubmitError(EDIT_BOOKING_CODES.WINDOW_INVALID);
      return undefined;
    }
    const end = new Date(start.getTime() + SESSION_LENGTH_MS);
    setSubmitting(true);
    setSubmitError(null);
    return editUpcomingBooking({
      transactionId: transaction?.id?.uuid || transaction?.id,
      sessionFormat: format,
      bookingStart: start.toISOString(),
      bookingEnd: end.toISOString(),
    })
      .then(payload => {
        setSubmitting(false);
        const data = payload?.data || payload;
        const nextId = bookingPageIdAfterEdit(data, transaction?.id?.uuid || transaction?.id);
        if (typeof onEdited === 'function') {
          onEdited(data);
        }
        onClose();
        if (nextId) {
          history.push(
            createResourceLocatorString(
              'OrderDetailsPage',
              routeConfiguration,
              { id: nextId },
              { edited: '1' }
            )
          );
        }
      })
      .catch(error => {
        setSubmitting(false);
        const apiCode = error?.code || error?.data?.errors?.[0]?.code || error?.data?.code || null;
        setSubmitError(apiCode || 'failed');
      });
  };

  return (
    <Modal
      id="EditBookingModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      containerClassName={css.container}
      contentClassName={css.content}
    >
      <FinalForm
        key={`edit-booking-${currentStart?.getTime() || 'none'}-${currentFormat || 'none'}`}
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={({ handleSubmit, values, form }) => {
          const dateKey = values.bookingDate;
          const openDays = openDateKeys(slots, timeZone, currentStart);
          const slotTimes = dateKey ? quarterHourStartsOnDate(slots, dateKey, timeZone) : [];
          const planWindows = dateKey
            ? planWindowsOnDate(listing?.attributes?.availabilityPlan, dateKey, timeZone)
            : [];
          const openTimes = filterStartsByPlanWindows(slotTimes, planWindows);
          const times = dateKey ? withCurrentStart(openTimes, currentStart, dateKey, timeZone) : [];
          const selectedStart = resolveEditStart(values.bookingStartTime, currentStart, {
            dateKey,
            currentDateKey,
          });
          const nextFormat = values.sessionFormat || currentFormat;
          const currentAmount = SESSION_FORMAT_PRICES_SUBUNITS[currentFormat];
          const nextAmount = SESSION_FORMAT_PRICES_SUBUNITS[nextFormat];
          const priceChanged = currentAmount !== nextAmount;
          const dirty = isEditBookingDirty({
            nextFormat,
            currentFormat,
            selectedStart,
            currentStart,
            dateKey,
            currentDateKey,
            bookingStartTime: values.bookingStartTime,
          });
          const canSave = dirty && !!selectedStart && !submitting;
          return (
            <Form onSubmit={handleSubmit} className={css.form}>
              <p className={css.kicker}>
                <FormattedMessage id="EditBookingModal.kicker" />
              </p>
              <h2 className={css.title}>
                <FormattedMessage id="EditBookingModal.title" values={{ name: listenerName }} />
              </h2>
              <p className={css.lede}>
                <FormattedMessage
                  id="EditBookingModal.lede"
                  values={{ minutes: SESSION_LENGTH_MINUTES }}
                />
              </p>
              <p className={css.current}>
                <FormattedMessage
                  id="EditBookingModal.current"
                  values={{
                    format: sessionFormatDisplayName(currentFormat),
                    when: whenLabel(currentStart, intl, timeZone),
                    price: moneyLabel(currentAmount),
                  }}
                />
              </p>

              <p className={css.fieldLabel} id="edit-booking-selected-label">
                <FormattedMessage id="EditBookingModal.selectedWhenLabel" />
              </p>
              <p
                className={css.selectedWhen}
                role="status"
                aria-labelledby="edit-booking-selected-label"
                data-edit-selected-when="true"
                data-edit-date={dateKey || ''}
                data-edit-time={values.bookingStartTime || ''}
              >
                {selectedStart
                  ? selectedWhenLabel(selectedStart, intl, timeZone)
                  : dateKey
                  ? intl.formatDate(middayOnDateKey(dateKey), {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      timeZone,
                    })
                  : intl.formatMessage({ id: 'EditBookingModal.pickTime' })}
              </p>

              <p className={css.fieldLabel} id="edit-booking-date-label">
                <FormattedMessage id="EditBookingModal.dateLabel" />
              </p>
              <div
                className={css.dayChips}
                role="radiogroup"
                aria-labelledby="edit-booking-date-label"
                data-edit-day-chips="true"
              >
                {openDays.length === 0 && !slotsLoading ? (
                  <p className={css.chipsEmpty}>
                    <FormattedMessage id="EditBookingModal.daysEmpty" />
                  </p>
                ) : null}
                {openDays.map(dayKey => {
                  const dayAt = middayOnDateKey(dayKey);
                  const selected = dayKey === dateKey;
                  return (
                    <button
                      key={dayKey}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={classNames(css.dayChip, selected ? css.dayChipSelected : null)}
                      data-edit-day={dayKey}
                      onClick={() => {
                        form.change('bookingDate', dayKey);
                        if (dayKey === currentDateKey && currentStart) {
                          form.change('bookingStartTime', String(currentStart.getTime()));
                          return;
                        }
                        const first = quarterHourStartsOnDate(slots, dayKey, timeZone)[0];
                        form.change('bookingStartTime', first ? String(first.getTime()) : '');
                      }}
                    >
                      <span className={css.dayChipDow}>
                        {intl.formatDate(dayAt, { weekday: 'short', timeZone })}
                      </span>
                      <span className={css.dayChipDom}>
                        {intl.formatDate(dayAt, { month: 'short', day: 'numeric', timeZone })}
                      </span>
                    </button>
                  );
                })}
              </div>

              <p className={css.fieldLabel} id="edit-booking-time-label">
                <FormattedMessage id="EditBookingModal.timeLabel" />
              </p>
              {times.length === 0 && slotsLoading ? (
                <p className={css.chipsEmpty}>
                  <FormattedMessage id="EditBookingModal.timesLoading" />
                </p>
              ) : times.length === 0 ? (
                <p className={css.chipsEmpty}>
                  <FormattedMessage id="EditBookingModal.timesEmpty" />
                </p>
              ) : (
                <TimeChipList
                  times={times}
                  selectedValue={values.bookingStartTime}
                  bookedStart={
                    dateKey === currentDateKey ? currentStart : null
                  }
                  timeZone={timeZone}
                  intl={intl}
                  onPick={value => form.change('bookingStartTime', value)}
                />
              )}

              <div className={css.formatWrap}>
                <SessionFormatPicker
                  priceVariants={priceVariants}
                  offeredFormats={offeredFormats}
                />
              </div>

              {selectedStart && !Number.isNaN(selectedStart.getTime()) ? (
                <p className={css.preview} data-edit-preview="true">
                  <FormattedMessage
                    id="EditBookingModal.preview"
                    values={{
                      format: sessionFormatDisplayName(nextFormat),
                      when: whenLabel(selectedStart, intl, timeZone),
                      price: moneyLabel(nextAmount),
                    }}
                  />
                </p>
              ) : null}
              {priceChanged ? (
                <p className={css.holdNote}>
                  <FormattedMessage
                    id="EditBookingModal.holdChange"
                    values={{
                      from: moneyLabel(currentAmount),
                      to: moneyLabel(nextAmount),
                    }}
                  />
                </p>
              ) : (
                <p className={css.holdNote}>
                  <FormattedMessage
                    id="EditBookingModal.holdSame"
                    values={{ price: moneyLabel(nextAmount) }}
                  />
                </p>
              )}

              {slotsError ? (
                <p className={css.error} role="alert">
                  {editBookingErrorMessage(slotsError)}
                </p>
              ) : null}
              {submitError ? (
                <p className={css.error} role="alert">
                  {editBookingErrorMessage(submitError)}
                </p>
              ) : null}

              <p className={css.finePrint}>
                <FormattedMessage id="EditBookingModal.finePrint" />
              </p>
              <div className={css.saveBar} data-edit-save-bar="true">
                <PrimaryButton
                  type="submit"
                  className={classNames(css.saveButton, canSave ? css.saveButtonEnabled : null)}
                  inProgress={submitting}
                  disabled={!canSave}
                  data-edit-save="true"
                  data-edit-confirm="true"
                >
                  <FormattedMessage id="EditBookingModal.confirm" />
                </PrimaryButton>
                <SecondaryButton type="button" className={css.keepCurrent} onClick={onClose}>
                  <FormattedMessage id="EditBookingModal.cancel" />
                </SecondaryButton>
                {typeof onCancelBooking === 'function' ? (
                  <button
                    type="button"
                    className={css.cancelThisBooking}
                    data-edit-cancel-booking="true"
                    onClick={() => {
                      onClose();
                      onCancelBooking();
                    }}
                  >
                    <FormattedMessage id="EditBookingModal.cancelThisBooking" />
                  </button>
                ) : null}
              </div>
            </Form>
          );
        }}
      />
    </Modal>
  );
};

export default EditBookingModal;
